import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

type Props = {
  open?: boolean;
  children: React.ReactNode;
  allowSave?: boolean;
  allowRemove?: boolean;
  onSave?: () => void;
  onCancel: () => void;
  onRemove?: () => void;
  saveText?: string;
  cancelText?: string;
  removeText?: string;
  showButtons?: boolean;
  id?: number;
  saveColor?: string;
  cancelColor?: string;
  removeColor?: string;
  data?: any;
  disableCancel?: boolean;
  additionalButtons?: JSX.Element;
};

const Modal = ({
  children,
  open = false,
  allowSave = true,
  allowRemove = true,
  saveText = 'Save Changes',
  cancelText = 'Cancel',
  removeText = 'Remove',
  onSave,
  onCancel,
  onRemove,
  showButtons,
  id,
  saveColor = 'bg-ptsblue-normal hover:bg-ptsblue-dark',
  cancelColor = 'bg-white text-black hover:bg-gray-50',
  removeColor = 'text-white bg-ptsblue-normal hover:bg-ptsblue-dark',
  disableCancel = false,
  additionalButtons = <></>,
  // eslint-disable-next-line no-shadow-restricted-names
  data: undefined,
}: Props) => {
  showButtons = showButtons ?? true;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        className='fixed inset-0 z-10 overflow-y-auto'
        // Passing empty function keeps dialog open when user clicks outside it.
        // Close function is handled exclusively by "cancel" button.
        onClose={() => { }}
      >
        <div className='flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75' />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className='hidden sm:inline-block sm:align-middle sm:h-screen'
            aria-hidden='true'
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div className='inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full'>
              {/* if we aren't showing buttons don't wrap the content in a padding, assuming the parent will handle that */}
              {!showButtons && (
                <div>
                  {children}
                </div>
              )}

              {/* if we are showing the buttons then show the content inside of padding */}
              {showButtons && (
                <Fragment>
                  <div className='px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4'>
                    {children}
                  </div>
                  <div className='px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse'>
                    {onSave ?
                      <button
                        type='button'
                        disabled={!allowSave}
                        className={`w-full inline-flex justify-right rounded-md border border-transparent shadow-sm px-4 py-2 bg-${saveColor} hover:bg-${saveColor} text-base font-medium text-white focus:outline-none sm:ml-3 sm:w-auto sm:text-sm  transition ease-in-out duration-150`}
                        onClick={onSave}
                      >
                        {saveText}
                      </button>
                      : ''
                    }
                    {onRemove && removeText !== undefined ?
                      <button
                        type='button'
                        disabled={!allowRemove || !onRemove}
                        className={`w-full inline-flex justify-left rounded-md border border-transparent shadow-sm px-4 py-2 ${removeColor} text-base font-medium text-white focus:outline-none sm:ml-3 sm:w-auto sm:text-sm  transition ease-in-out duration-150`}
                        onClick={onRemove}
                      >
                        {removeText}
                      </button>
                      :
                      ''
                    }
                    {!disableCancel &&
                      <button
                        type='button'
                        className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 font-medium ${cancelColor} hover:bg-${cancelColor} focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm  transition ease-in-out duration-150`}
                        onClick={onCancel}
                      >
                        {cancelText}
                      </button>
                    }
                    {additionalButtons}
                  </div>
                </Fragment>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
